import { useRef } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

import { color, movimiento } from './tokens';

/** Milisegundos de la hoja bajando sola hasta salir. */
const MS_SALIDA = 200;

/**
 * ¿Se cierra al soltar? Si se ha bajado una cuarta parte de la hoja (al
 * menos 80 px) o se ha lanzado hacia abajo con algo de prisa.
 */
export function debeCerrarse(
  bajado: number,
  altoHoja: number,
  velocidad: number,
): boolean {
  if (bajado >= Math.max(80, altoHoja * 0.25)) {
    return true;
  }
  return velocidad > 0.5 && bajado > 24;
}

type Gesto = {
  hoja: HTMLElement;
  y0: number;
  yAnterior: number;
  tAnterior: number;
  velocidad: number;
  bajado: number;
};

/**
 * Asa de la hoja: la barrita de arriba. Se agarra y se baja para cerrar la
 * hoja (como en las apps nativas); si no se baja lo bastante, vuelve a su
 * sitio. Va como primer hijo de la hoja: mueve a su padre. La zona de agarre ocupa todo el ancho de la parte de arriba.
 */
export function AsaHoja({ onCerrar }: { onCerrar: () => void }) {
  const gesto = useRef<Gesto | null>(null);

  const mover = (hoja: HTMLElement, y: number, transicion?: string) => {
    hoja.style.transition = transicion ?? 'none';
    hoja.style.transform = y === 0 ? '' : `translateY(${y}px)`;
  };

  const onPointerDown = (ev: ReactPointerEvent<HTMLDivElement>) => {
    const hoja = ev.currentTarget.parentElement;
    if (!hoja) {
      return;
    }
    ev.currentTarget.setPointerCapture(ev.pointerId);
    gesto.current = {
      hoja,
      y0: ev.clientY,
      yAnterior: ev.clientY,
      tAnterior: ev.timeStamp,
      velocidad: 0,
      bajado: 0,
    };
  };
  const onPointerMove = (ev: ReactPointerEvent<HTMLDivElement>) => {
    const g = gesto.current;
    if (!g) {
      return;
    }
    const dy = ev.clientY - g.y0;
    // Hacia arriba apenas se mueve: la hoja no sube más.
    g.bajado = dy > 0 ? dy : dy / 6;
    const dt = Math.max(1, ev.timeStamp - g.tAnterior);
    g.velocidad = (ev.clientY - g.yAnterior) / dt;
    g.yAnterior = ev.clientY;
    g.tAnterior = ev.timeStamp;
    mover(g.hoja, g.bajado);
  };
  const onPointerUp = () => {
    const g = gesto.current;
    gesto.current = null;
    if (!g) {
      return;
    }
    if (debeCerrarse(g.bajado, g.hoja.offsetHeight, g.velocidad)) {
      mover(
        g.hoja,
        g.hoja.offsetHeight + 40,
        `transform ${MS_SALIDA}ms ease-in`,
      );
      window.setTimeout(() => {
        onCerrar();
        // Si la hoja no se ha cerrado (p. ej. está guardando), vuelve.
        window.setTimeout(() => {
          if (g.hoja.isConnected) {
            mover(
              g.hoja,
              0,
              `transform ${movimiento.hoja}ms ${movimiento.suave}`,
            );
          }
        }, 300);
      }, MS_SALIDA);
      return;
    }
    mover(g.hoja, 0, `transform ${movimiento.hoja}ms ${movimiento.suave}`);
  };

  return (
    <div
      // Solo gesto: la ✕, el velo, Escape y «atrás» también cierran.
      aria-hidden
      data-testid="hoja-asa"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{
        // Todo el ancho de arriba (la hoja tiene 8 px arriba y 16 a los lados).
        margin: '-8px -16px 0',
        height: 28,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        touchAction: 'none',
        cursor: 'grab',
      }}
    >
      <div
        style={{
          width: 36,
          height: 4,
          borderRadius: 9,
          backgroundColor: color.line2,
        }}
      />
    </div>
  );
}
